/** Stratul 3D al orașului: un grup gata construit, pus în macheta de situație
 *  și scos din ea fără să atingă restul scenei.
 *
 *  Se construiește O DATĂ pe hartă (nu pe scenă): macheta se reface la fiecare
 *  revizie a soluției, iar retriangularea a mii de clădiri la fiecare rescriere
 *  ar costa secunde. `ataseaza` îl mută în scena de acum; `elibereaza` îl
 *  dărâmă când se schimbă harta sau se închide macheta.
 *
 *  Culorile sunt cele din `culori.ts`, comune cu planul 2D. Același model de
 *  lumină ca restul machetei (mat, fără luciu), altfel lumina cerului n-ar
 *  ajunge pe oraș și albul ar ieși albăstrui lângă clădirea noastră. Macheta
 *  trebuie să meargă și pe o mașină fără placă video: de aceea geometria e
 *  strânsă pe celule (`geometrie.ts`).
 *
 *  ORAȘUL NU ARUNCĂ UMBRE, doar le primește pe ale clădirilor noastre, în jurul
 *  lotului (`RAZA_UMBRE_M`). Harta de umbre a soarelui e un pătrat centrat pe
 *  lot, cât să cuprindă umbrele proiectului (v. `puneSoare` în `RandareSit3D`):
 *  o clădire a orașului de la marginea lui își arunca umbra retezată drept,
 *  iar una din afară deloc — un oraș pe jumătate umbrit, care se citește greșit.
 */

import * as THREE from "three";
import { CELULA_M, geometrieCladiri, geometrieSuprafete, peCelule } from "./geometrie";
import { construiestePomi } from "../plan/copaci3d";
import { CULORI } from "./culori";
import { vegetatieOras } from "./vegetatie";
import type { Oras3, Suprafata3 } from "./tipuri";

/** Până unde, de la centrul lotului, orașul primește umbrele clădirilor noastre. */
export const RAZA_UMBRE_M = 600;

// COTELE LA SOL, sub tot ce e al lotului (care stă de la 0 în sus) și peste
// terenul machetei (−0,05): suprafețele hărții se văd în jurul lotului, iar pe
// lot le acoperă desenul lui. Câte un centimetru între ele: cu tamponul de
// adâncime logaritmic al machetei, la 2 km asta înseamnă ~1 mm de rezoluție.
const Z = { verde: -0.045, apa: -0.044, pietonal: -0.04, strada: -0.035,
            caleFerata: -0.03, placa: -0.08 };

export interface StratOras {
  grup: THREE.Group;
  ceata: { culoare: number; aproape: number; departe: number };
  raza: number;
  elibereaza: () => void;
}

export function construiesteOras(o: Oras3): StratOras {
  const grup = new THREE.Group();
  grup.name = "oras";
  const deArs: { dispose: () => void }[] = [];
  const mat = (culoare: number) => {
    const m = new THREE.MeshStandardMaterial({ color: culoare, roughness: 0.95, metalness: 0 });
    deArs.push(m);
    return m;
  };
  const M = {
    osm: mat(CULORI.osm), verificat: mat(CULORI.verificat), estimat: mat(CULORI.estimat),
    strada: mat(CULORI.strada), pietonal: mat(CULORI.pietonal),
    caleFerata: mat(CULORI.caleFerata), verde: mat(CULORI.verde),
    apa: mat(CULORI.apa), teren: mat(CULORI.teren),
  };
  const [cx, cy] = o.centru;
  const aproape = (x: number, y: number) =>
    Math.hypot(x - cx, y - cy) <= RAZA_UMBRE_M + CELULA_M * 0.75;

  // clădirile, pe celule și pe culoare
  const celule = peCelule(o.cladiri, (c) => [c.cx, c.cy]);
  for (const lista of celule.values()) {
    for (const sursa of ["osm", "verificat", "estimat"] as const) {
      const parte = lista.filter((c) => c.sursa === sursa);
      const g = geometrieCladiri(parte);
      if (!g) continue;
      deArs.push(g);
      const m = new THREE.Mesh(g, M[sursa]);
      const s = g.boundingSphere;
      m.castShadow = false;
      m.receiveShadow = s ? aproape(s.center.x, s.center.y) : false;
      m.matrixAutoUpdate = false;
      grup.add(m);
    }
  }

  // suprafețele de la sol, tot pe celule (după primul punct)
  const sol = (lista: Suprafata3[], z: number, material: THREE.Material) => {
    for (const parte of peCelule(lista, (s) => [s.inele[0][0][0], s.inele[0][0][1]]).values()) {
      const g = geometrieSuprafete(parte, z);
      if (!g) continue;
      deArs.push(g);
      const m = new THREE.Mesh(g, material);
      const s = g.boundingSphere;
      m.receiveShadow = s ? aproape(s.center.x, s.center.y) : false;
      m.matrixAutoUpdate = false;
      grup.add(m);
    }
  };
  sol(o.verde, Z.verde, M.verde);
  sol(o.apa, Z.apa, M.apa);
  sol(o.pietonale, Z.pietonal, M.pietonal);
  sol(o.strazi, Z.strada, M.strada);
  sol(o.caiFerate, Z.caleFerata, M.caleFerata);

  // PLACA DE SUB ORAȘ: terenul machetei e cât amplasamentul de câteva ori;
  // harta trece mult de el
  const geoPlaca = new THREE.CircleGeometry(o.raza * 1.4, 64);
  geoPlaca.translate(cx, cy, Z.placa);
  deArs.push(geoPlaca);
  const placa = new THREE.Mesh(geoPlaca, M.teren);
  placa.matrixAutoUpdate = false;
  grup.add(placa);

  // POMII ȘI BOSCHEȚII ORAȘULUI, în parcurile din jurul lotului; ca și
  // clădirile orașului, nu aruncă umbre
  const veg = vegetatieOras(o);
  if (veg.copaci.length || veg.tufisuri.length) {
    const pomi = construiestePomi(veg.copaci, veg.tufisuri, { umbre: false });
    deArs.push(...pomi.deArs);
    grup.add(...pomi.obiecte);
  }

  grup.updateMatrixWorld(true);
  return {
    grup,
    ceata: { culoare: CULORI.ceata, aproape: o.raza * 0.75, departe: o.raza * 1.35 },
    raza: o.raza,
    elibereaza: () => {
      grup.removeFromParent();
      deArs.forEach((d) => d.dispose());
    },
  };
}

/** Ce trebuie din macheta de acum ca stratul să intre în ea. */
export interface GazdaOras {
  lume: THREE.Object3D;
  cam: THREE.PerspectiveCamera;
  ctrl?: { maxDistance: number };
  /** Cât stă terenul SUB ±0,00 (soclul clădirii, v. `ScenaSit.ridicare`):
   *  orașul stă pe teren, nu la cota parterului. */
  teren?: number;
}

/** Pune stratul în macheta de acum: în `lume` (cadrul documentului), cu ceața
 *  la marginea hărții și camera lăsată să se depărteze cât să vadă orașul.
 *  Întoarce desfacerea — tot ce a schimbat, pus la loc. */
export function ataseaza(strat: StratOras, gazda: GazdaOras): () => void {
  const scena = gazda.lume.parent as THREE.Scene | null;
  strat.grup.position.z = -(gazda.teren ?? 0);
  strat.grup.updateMatrix();
  gazda.lume.add(strat.grup);
  const ceataVeche = scena?.fog ?? null;
  if (scena) {
    scena.fog = new THREE.Fog(strat.ceata.culoare, strat.ceata.aproape, strat.ceata.departe);
  }
  const departe = gazda.cam.far;
  gazda.cam.far = Math.max(departe, strat.raza * 3);
  gazda.cam.updateProjectionMatrix();
  const maxDist = gazda.ctrl?.maxDistance;
  if (gazda.ctrl && maxDist != null) gazda.ctrl.maxDistance = Math.max(maxDist, strat.raza * 1.2);
  return () => {
    gazda.lume.remove(strat.grup);
    if (scena) scena.fog = ceataVeche;
    gazda.cam.far = departe;
    gazda.cam.updateProjectionMatrix();
    if (gazda.ctrl && maxDist != null) gazda.ctrl.maxDistance = maxDist;
  };
}
